import { useState } from 'react'
import {
    LightningIcon,
    MagnifyingGlassIcon,
    ArrowClockwiseIcon,
    PaperPlaneRightIcon,
} from '@phosphor-icons/react'
import { ScoopText, ScoopButton, ScoopColumn, ScoopRow } from '../../../../ui'
import styles from './AssistantMessage.module.scss'

export const AssistantMessage = ({
    message,
    onQuickAnalysis,
    onDeepAnalysis,
    followUpPrompts,
    onSendFollowUp,
}) => {
    const [selectionMade, setSelectionMade] = useState(false)

    // Check if this is an error message
    const isError = message.role === 'error'
    const requiresRefresh = message.requiresRefresh === true

    // Build the display text for errors
    const displayText =
        isError && followUpPrompts?.length > 0
            ? `${message.text}\n\nYou can try one of the suggested prompts below to continue.`
            : message.text

    const handleRefresh = () => {
        window.location.reload()
    }

    const handleDatasetSelection = (datasetName) => {
        if (!selectionMade && onSendFollowUp) {
            setSelectionMade(true)
            onSendFollowUp(`Tell me about ${datasetName}`)
        }
    }

    return (
        <ScoopRow className={styles.container}>
            <ScoopColumn className={styles.content}>
                <ScoopText className={styles.messageText}>{displayText}</ScoopText>

                {/* Show refresh button if error requires refresh */}
                {requiresRefresh && (
                    <div className={styles.refreshContainer}>
                        <ScoopButton
                            variant="default"
                            leftSection={<ArrowClockwiseIcon size={16} />}
                            onClick={handleRefresh}
                            className={styles.refreshButton}
                        >
                            Refresh Browser
                        </ScoopButton>
                    </div>
                )}

                {/* Show follow-up actions if this was a NEEDS_REASONING response */}
                {message.needsReasoning && (
                    <div className={styles.reasoningContainer}>
                        <ScoopText className={styles.containerHeader}>
                            Choose analysis type:
                        </ScoopText>
                        <ScoopColumn className={styles.optionsContainer}>
                            <ScoopRow
                                onClick={() => {
                                    if (!selectionMade) {
                                        setSelectionMade(true)
                                        onQuickAnalysis(message.originalQuery)
                                    }
                                }}
                                className={`${styles.analysisOption} ${selectionMade ? styles.disabled : ''}`}
                            >
                                <ScoopColumn className={styles.optionDetails}>
                                    <ScoopText className={styles.optionTitle}>
                                        Quick Analysis
                                    </ScoopText>
                                    <ScoopText className={styles.optionDescription}>
                                        Fast response (~10 seconds)
                                    </ScoopText>
                                </ScoopColumn>
                                <LightningIcon size={16} color={styles.colorOrange} />
                            </ScoopRow>

                            <ScoopRow
                                onClick={() => {
                                    if (!selectionMade) {
                                        setSelectionMade(true)
                                        onDeepAnalysis(message.originalQuery)
                                    }
                                }}
                                className={`${styles.analysisOption} ${selectionMade ? styles.disabled : ''}`}
                            >
                                <ScoopColumn className={styles.optionDetails}>
                                    <ScoopText className={styles.optionTitle}>
                                        Deep Analysis
                                    </ScoopText>
                                    <ScoopText className={styles.optionDescription}>
                                        Comprehensive insights ({'>'}30 seconds)
                                    </ScoopText>
                                </ScoopColumn>
                                <MagnifyingGlassIcon size={16} color={styles.colorPrimary} />
                            </ScoopRow>
                        </ScoopColumn>
                    </div>
                )}

                {/* Show dataset selection options */}
                {message.needsDatasetSelection && message.datasetAlternatives?.length > 0 && (
                    <div className={styles.reasoningContainer}>
                        <ScoopText className={styles.containerHeader}>Select a dataset:</ScoopText>
                        <ScoopColumn className={styles.optionsContainer}>
                            {message.datasetAlternatives.map((dataset, i) => (
                                <ScoopRow
                                    key={i}
                                    onClick={() => handleDatasetSelection(dataset.dataset)}
                                    className={`${styles.analysisOption} ${selectionMade ? styles.disabled : ''}`}
                                >
                                    <ScoopColumn className={styles.optionDetails}>
                                        <ScoopText className={styles.optionTitle}>
                                            {dataset.dataset}
                                        </ScoopText>
                                        {dataset.description && (
                                            <ScoopText className={styles.optionDescription}>
                                                {dataset.description}
                                            </ScoopText>
                                        )}
                                    </ScoopColumn>
                                    <PaperPlaneRightIcon size={14} className={styles.sendIcon} />
                                </ScoopRow>
                            ))}
                        </ScoopColumn>
                    </div>
                )}

                {/* Show follow-up prompts for error messages */}
                {isError && followUpPrompts?.length > 0 && onSendFollowUp && (
                    <div className={styles.followUpContainer}>
                        <ScoopText className={styles.containerHeader}>
                            Suggested follow-ups:
                        </ScoopText>
                        <ScoopColumn className={styles.optionsContainer}>
                            {followUpPrompts.map((prompt, i) => (
                                <ScoopRow
                                    key={i}
                                    onClick={() => onSendFollowUp(prompt.prompt || prompt)}
                                    className={styles.followUpItem}
                                >
                                    <ScoopText className={styles.followUpText}>
                                        {prompt.prompt || prompt}
                                    </ScoopText>
                                    <PaperPlaneRightIcon size={14} className={styles.sendIcon} />
                                </ScoopRow>
                            ))}
                        </ScoopColumn>
                    </div>
                )}
            </ScoopColumn>
        </ScoopRow>
    )
}
